import { AppContext } from "@/App.context";
import config from "@/config";
import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import {
  API_LOADING_ERROR,
  API_NO_DATA_FOUND_ERROR,
  API_NOT_FOUND_ERROR,
  ERROR_STATUS_CODES,
} from "@/lib/errors/error-constants";

/**
 * @typedef Recommendation
 * @property {string} account
 * @property {string} resource_name
 * @property {string} resourceGroupName
 * @property {string} resourceId
 * @property {string} region
 * @property {string} current_type
 * @property {string} owner
 * @property {string} projected_type
 * @property {number} savings
 * @property {number} riSavings
 * @property {number} utilization_cpu
 * @property {number} utilization_mem
 * @property {number} utilization_disk
 * @property {number} cost
 * @property {string} status
 * @property {number} history
 */

/**
 * @typedef GetRecommendationsListRes
 * @property {Recommendation[]} recommendation
 * @property {number} total
 */

/**
 * @typedef GetRecommendationsFilters
 * @property {string[]} accounts
 * @property {string[]} regions
 */

/**
 * @typedef GetRecommendationsListReq
 * @property {'aws' | 'gcp' | 'azure' | 'all'} service
 * @property {number} page
 * @property {number} limit
 * @property {string} orderBy
 * @property {string} desc
 */

export const getRecommendationsList = (token) => {
  /**
   * @param {GetRecommendationsListReq} req
   * @returns {Promise<GetRecommendationsListRes>}
   */
  return async ({ page, limit, service, orderBy, desc }, signal) => {
    const recommendationsList = await fetch(
      `${config.apiHost}/v1/${service}/recommendations/get-all-discarded`,
      {
        method: "POST",
        body: JSON.stringify({
          page,
          limit,
          orderBy,
          desc,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        signal,
      }
    );
    if (!recommendationsList.ok) {
      if (ERROR_STATUS_CODES.includes(recommendationsList.status)) {
        throw new Error(API_LOADING_ERROR);
      }
      if (recommendationsList.status === 404) {
        throw new Error(API_NOT_FOUND_ERROR);
      }
      throw new Error((await recommendationsList.json()).message);
    }
    const data = await recommendationsList.json();

    if (data?.recommendation.length === 0) {
      throw new Error(API_NO_DATA_FOUND_ERROR);
    }
    return data;
  };
};

/**
 * @param {GetRecommendationsListReq} req
 * @param {any[]=} deps
 */
export const useDiscardedTable = (req, deps = []) => {
  const appContext = useContext(AppContext);
  const query = useQuery({
    queryKey: ["getRecommendationsList", req, ...deps],
    queryFn: ({ signal }) =>
      getRecommendationsList(appContext.idToken)(req, signal),
  });
  return query;
};
